import { inject, NetworkException } from "fw";
import { handle } from "fw-state";
import { StartAction } from "./actions";
import { FetchListAction, GridStore, RefreshAction } from "./components/grid";
import { AccountType, AtsOrganization, AtsOrganizationFilter, FilterKeys } from "models/ats-organization";
import { AtsOrgSummaryRepository } from "network/ats-org-summary-repository";
import { once } from "../helpers/once";
import { AtsOrganizationSummary } from "models/ats-organization-summary";
import { escapeLuceneSpecialCharacters } from "helpers/utils";

const FILTER_FIELDS_MAPPING: Map<string, string> = new Map().set("type", "metaData.type").set("tags", "tags");

const CACHE_KEY = "ats-org-list-store";
const MAX_SKIP = 2000;

export class OrgListCollapseItemAction {
    constructor(public id: string) {}
}
export class PreviousPageAction {}
export class NextPageAction {}

export class OrgFetchListAction<Filter> {
    constructor(
        public filter: Filter = null,
        public search: string = null,
        public sort: string = ""
    ) {}
}
export class OrgListEnsureAction {}
export class OrgListRefreshAction {}
export class OrgListResetAction {}
export class OrgListSelectOrg {
    constructor(public organization: AtsOrganization | {}) {}
}

export class OrgListFetchListAction<Filter> {
    constructor(
        public filter: Filter = null,
        public search: string = null,
        public sort: string = ""
    ) {}
}
export class OrgListUpdateRefreshDate {
    constructor(public date: Date = new Date()) {}
}

export class SelectedFilterAction {
    constructor(
        public key: number,
        public data: any
    ) {}
}

@inject
export class AtsOrganizationGridStore extends GridStore<AtsOrganizationSummary, AtsOrganizationFilter> {
    constructor(private network: AtsOrgSummaryRepository) {
        super(CACHE_KEY);
    }

    @handle(StartAction)
    private handleActionStart(action: StartAction) {
        this.handleStartAction(action);
    }

    @handle(SelectedFilterAction)
    private handleSelectedOrganizationFilter(action: SelectedFilterAction) {
        this.setState((state) => ({
            ...state,
            selectedFilter: {
                ...state.selectedFilter,
                [action.key]: action.data
            }
        }));
    }

    @handle(NextPageAction)
    private nextPageAction(action: NextPageAction) {
        const canAdvancePage = this.state["canAdvancePage"];
        const currentPage = this.state.pageNumber || 0;
        this.setState((state) => ({
            ...state,
            currentUserId: this.state.currentUserId,
            pageNumber: canAdvancePage ? currentPage + 1 : currentPage,
            loaded: false
        }));

        this.loadOrganizationData();
    }

    @handle(PreviousPageAction)
    private previousPageAction(action: PreviousPageAction) {
        const canAdvancePage = this.state["canAdvancePage"];
        const currentPage = this.state.pageNumber || 0;
        this.setState((state) => ({
            ...state,
            currentUserId: this.state.currentUserId,
            pageNumber: canAdvancePage ? currentPage - 1 : currentPage,
            loaded: false
        }));

        this.loadOrganizationData();
    }

    @handle(OrgListEnsureAction)
    private async handleAtsOrganizationEnsureAction() {
        await once(CACHE_KEY, async () => {
            if (this.state.loaded) return;
            this.restoreState();
            this.loadOrganizationData();
        });
    }

    @handle(OrgListResetAction)
    private resetAction(action: OrgListResetAction) {
        this.setState((state) => ({
            ...state,
            selectedFilter: { [FilterKeys.selectedTypes]: AccountType.Customer }
        }));
    }

    @handle(OrgListRefreshAction)
    private refreshAction(action: RefreshAction) {
        this.handleRefreshAction(action, () => {
            this.restoreState();
            this.loadOrganizationData();
        });
    }

    @handle(OrgFetchListAction)
    private fetchListAction(action: FetchListAction<AtsOrganizationFilter>) {
        this.handleFetchList(action, () => {
            this.loadOrganizationData();
        });
    }

    private async loadOrganizationData() {
        const { pageSize, sort, search, filter } = this.state;
        let currentPage = this.state.pageNumber || 0;
        const hasErrors = filter.mapping ? filter.mapping === "hasErrors" : null;

        try {
            const cleanSearchText = escapeLuceneSpecialCharacters(search);
            const res = filter.mapping
                ? await this.network.searchWithMappings(
                      hasErrors,
                      this.getWholeOrgListFilter(true, FILTER_FIELDS_MAPPING),
                      cleanSearchText,
                      sort,
                      currentPage,
                      pageSize
                  )
                : await this.network.search(this.getWholeOrgListFilter(true, FILTER_FIELDS_MAPPING), cleanSearchText, sort, currentPage, pageSize);

            const total = res.total;

            const lastPage = Math.ceil(total / pageSize);
            currentPage = Math.min(currentPage, lastPage);

            const pageStartNumber = (currentPage - 1) * pageSize + 1;
            const pageEndNumber = pageStartNumber + (pageSize - 1);
            const hasNextPage = pageEndNumber < total;
            const hasPreviousPage = pageStartNumber > 1;
            const canAdvancePage = pageEndNumber + 1 < MAX_SKIP;

            this.setState((state) => ({
                ...state,
                currentUserId: this.state.currentUserId,
                loaded: true,
                errorLoading: false,
                errorMessage: "",
                list: res.list,
                total,
                pageNumber: currentPage,
                pageStartNumber: Math.max(pageStartNumber, 0),
                pageEndNumber: Math.min(pageEndNumber, total),
                hasNextPage,
                hasPreviousPage,
                canAdvancePage,
                lastPage
            }));
        } catch (err) {
            let message = "Error loading organization data";
            if (err instanceof NetworkException) {
                message += `: ${err.result?.message}`;
            }
            this.setState((state) => ({ ...state, loaded: true, list: [], errorLoading: true, errorMessage: message }));
        }
    }

    public getWholeOrgListFilter(withStatus = true, mapping): string {
        const ignoredFilters = ["mapping"];

        const { filter } = this.state;

        if (!Object.keys(filter).length) return null;

        const filterArgs = [];
        for (const [key, value] of Object.entries(filter)) {
            if (value === undefined || value === null) continue;

            if (ignoredFilters.includes(key)) continue;
            if (!withStatus && key === "status") continue;

            if (Array.isArray(value) && value.length) {
                (value as Array<string>).forEach((element) => {
                    filterArgs.push(`${mapping.get(key)}:"${element}"`);
                });
            } else {
                filterArgs.push(`${mapping.get(key)}:${value}`);
            }
        }

        return filterArgs.join(" AND ");
    }
}
